import { calendarSocialImageUrl } from "@stanza/shared/seo/social-image";
import { tileObjectPrefix } from "@stanza/shared/constants/image-derivatives";

/**
 * Canonical construction and normalization of image URLs. Every cache layer keys
 * off these helpers, so a URL built here matches the one a preload requested.
 */

const TIMESTAMP_PARAM = "timestamp";

/**
 * Host serving calendar art, creator avatars and event images. Read per call
 * rather than at module scope so tests can point it at a fixture host.
 * @returns {string|undefined}
 */
export const imageHost = () => process.env.REACT_APP_CLOUD_STORAGE_IMAGE_URL;

/**
 * Public CDN host for bucket-served art (logos, avatars), falling back to the
 * prod host when the env var is unset. Read per call so tests can override it.
 * @returns {string}
 */
export const publicImageHost = () =>
  process.env.REACT_APP_CLOUD_STORAGE_IMAGE_URL || "https://static.stanzacal.com";

/**
 * Splits a URL into its path and query parts.
 * @param {string} url
 * @returns {[string, string]}
 */
const splitQuery = (url) => {
  const index = url.indexOf("?");
  return index === -1 ? [url, ""] : [url.slice(0, index), url.slice(index + 1)];
};

/**
 * Cache key for an image's bytes: drops `timestamp` but keeps `version`, so a
 * post-upload version bump is a miss while a per-minute timestamp is not.
 * @param {string|null|undefined} url
 * @returns {string|null|undefined}
 */
export const canonicalImageKey = (url) => {
  if (!url || typeof url !== "string") return url;

  const [base, query] = splitQuery(url);
  if (!query) return url;

  const kept = query
    .split("&")
    .filter((pair) => pair && pair.split("=")[0] !== TIMESTAMP_PARAM);

  return kept.length ? `${base}?${kept.join("&")}` : base;
};

/**
 * Identity of the underlying asset, ignoring every query param. Use for card
 * identity and for comparing two renderings of the same image.
 * @param {string|null|undefined} url
 * @returns {string}
 */
export const imageIdentity = (url) => {
  if (!url || typeof url !== "string") return "";
  return splitQuery(url)[0];
};

/**
 * Resolves a stored `picture` value, which is either an absolute URL or a bare
 * filename, against the image host.
 * @param {string|null|undefined} picture
 * @returns {string|null}
 */
export const resolvePictureUrl = (picture) => {
  if (!picture) return null;
  return picture.includes("http") ? picture : `${imageHost()}/${picture}`;
};

/**
 * Profile art for a calendar, falling back to the handle-derived filename when
 * the creator's picture was not populated on the document.
 * @param {{ picture?: string, handle?: string }|null|undefined} calendar
 * @returns {string|null}
 */
export const calendarProfileImageUrl = (calendar) => {
  const picture =
    calendar?.picture ||
    (calendar?.handle ? `${calendar.handle}-profileImage.png` : null);
  return resolvePictureUrl(picture);
};

/**
 * Uploaded art for a single event.
 * @param {string|null|undefined} handle
 * @param {string|null|undefined} eventId
 * @returns {string|null}
 */
export const eventImageUrl = (handle, eventId) => {
  const host = imageHost();
  if (!handle || !eventId || !host) return null;
  return `${host}/${handle}-${eventId}-eventImage.png`;
};

/**
 * Rewrites a known CDN URL to its tile-sized variant. Decoded bitmaps live in
 * memory at source resolution, so tiles must not decode full-size art.
 * @param {string|null|undefined} url
 * @returns {string|null|undefined}
 */
export const tileSizedImage = (url) => {
  if (!url || typeof url !== "string") return url;
  // TVMaze deliberately stays on original_untouched: its only small variant
  // (~210px medium) upscales visibly grainy in retina poster tiles.
  const tmdb = url.replace(/(image\.tmdb\.org\/t\/p\/)[^/]+/, "$1w342");
  if (tmdb !== url) return tmdb;
  return url;
};

/** Poster tiles render ≤190px wide; 3x covers retina. Must be a width the
 *  resize endpoint allows. */
const RESIZE_WIDTH = 570;

/** base64url to match the resizer's bucket key scheme (Buffer base64url). */
const base64Url = (value) =>
  btoa(value).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/**
 * Display-sized derivative candidates for a heavy source image, best first:
 * the CDN-cached bucket object, then the resize endpoint that generates it on
 * a miss. Callers keep the original URL as their final fallback.
 * @param {string|null|undefined} url
 * @returns {string[]}
 */
const derivativeCandidates = (url, prefix, style) => {
  const candidates = [`${publicImageHost()}/${prefix}/${base64Url(url)}.webp`];
  const endpoint = process.env.REACT_APP_IMAGE_RESIZE_URL;
  if (endpoint) {
    candidates.push(`${endpoint}?style=${style}&url=${encodeURIComponent(url)}&w=${RESIZE_WIDTH}`);
  }
  return candidates;
};

export const resizedImageCandidates = (url) => {
  if (!url || typeof url !== "string") return [];
  // Only TVMaze originals for now — the one poster source with no usable
  // small variant of its own.
  if (!url.includes("static.tvmaze.com/uploads/images/original_untouched")) return [];
  return derivativeCandidates(url, `resized/w${RESIZE_WIDTH}`, "resize");
};

/**
 * Social-share card (og:image) for a calendar page, from the endpoint's
 * calendar-card mode at 1200x630. Undefined when the endpoint is unconfigured.
 * @param {string|null|undefined} calendarId
 * @returns {string|undefined}
 */
export const calendarOgImageUrl = (calendarId) =>
  calendarSocialImageUrl(process.env.REACT_APP_IMAGE_RESIZE_URL, calendarId);

/**
 * Server-baked sports-tile candidates (background-removed logo over its
 * palette gradient), best first. Empty for non-bucket sources; callers fall
 * back to the client pipeline when the chain exhausts.
 * @param {string|null|undefined} url
 * @returns {string[]}
 */
export const bakedTileCandidates = (url) => {
  if (!url || typeof url !== "string") return [];
  const host = publicImageHost();
  if (!host || !url.startsWith(`${host}/`)) return [];
  return derivativeCandidates(url, tileObjectPrefix(RESIZE_WIDTH), "tile");
};

/**
 * Appends a query param, picking the right separator for the URL.
 * @param {string} url
 * @param {string} param
 * @param {string|number} value
 * @returns {string}
 */
export const withParam = (url, param, value) => {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${param}=${value}`;
};

/**
 * Registers an img's session-cached settle state from a ref, which WebKit may
 * not re-fire load/error for on remount. A src-less img reads as failed.
 * @param {HTMLImageElement|null} img
 * @param {{ onLoaded?: () => void, onFailed?: () => void }} callbacks
 */
export const registerCachedImage = (img, { onLoaded, onFailed } = {}) => {
  if (!img || !img.complete) return;
  if (img.naturalWidth > 0) onLoaded?.();
  else onFailed?.();
};
